import PropTypes from "prop-types";
import React from "react";

import { classNames } from "@swa-ui/string";

import styles from "./Separator.module.scss";

const SEPARATOR_FG_DESIGN_TOKEN = "cmp-core-color-separator-fg";

/**
 * Simple component to render a horizontal or vertical line with options to control line's
 * thickness and its end caps.
 */
export const Separator = (props) => {
  const {
    alignment = "left",
    "aria-label": ariaLabel,
    children,
    className,
    color = SEPARATOR_FG_DESIGN_TOKEN,
    lineCap = "round",
    orientation = "horizontal",
    weight = "medium",
  } = props;

  return (
    <div aria-label={ariaLabel} className={getClass()} role="separator">
      {lineCap === "dot" ? <div {...getDotProps()} /> : null}
      <div {...getLineProps()} />
      {children ? <div className={getChildrenClass()}>{children}</div> : null}
      {lineCap === "dot" ? <div {...getDotProps(true)} /> : null}
    </div>
  );

  function getLineProps() {
    const size = getFromWeight(
      "var(--cmp-core-separator-small-weight-units-px)",
      "var(--cmp-core-separator-medium-weight-units-px)",
      "var(--cmp-core-separator-large-weight-units-px)"
    );

    return {
      className: styles.line,
      style: {
        "--separator-color": `var(--${color})`,
        "--separator-radius": lineCap === "round" && size,
        "--separator-weight": size,
      },
    };
  }

  function getDotProps(end) {
    return {
      className: classNames(styles.lineCapDot, {
        [styles.endCap]: end,
      }),
      style: {
        "--separator-color": `var(--${color})`,
        "--separator-dot-weight": getFromWeight(
          "var(--cmp-core-separator-small-dot-weight-units-px)",
          "var(--cmp-core-separator-medium-dot-weight-units-px)",
          "var(--cmp-core-separator-large-dot-weight-units-px)"
        ),
      },
    };
  }

  function getFromWeight(small, medium, large) {
    return weight === "small" ? small : weight === "medium" ? medium : large;
  }

  function getClass() {
    return classNames(className, styles.separator, {
      [styles.vertical]: orientation === "vertical",
    });
  }

  function getChildrenClass() {
    return classNames(styles.children, {
      [styles.alignCenter]: alignment === "center",
      [styles.alignLeft]: alignment === "left",
      [styles.alignRight]: alignment === "right",
    });
  }
};

Separator.displayName = "Separator";

export const separatorPropTypes = {
  /** Option to position children content horizontally along the separator. */
  alignment: PropTypes.oneOf(["center", "left", "right"]),

  /** aria-label for the separator element. */
  "aria-label": PropTypes.string,

  /** Optional content to be render on top of separator line. */
  children: PropTypes.node,

  /**
   * Additional classes for positioning the component. Given classes may only position this component
   * for layout purposes, and cannot change how the component renders in any way.
   */
  className: PropTypes.string,

  /**
   * Color for separator should be a theme definition such as primary-accordion-label, or a
   * standard color definition like success-300. A hex value will also work, but is discouraged.
   */
  color: PropTypes.string,

  /** Specify how separator ends will look. */
  lineCap: PropTypes.oneOf(["butt", "dot", "round"]),

  /** Direction of separator. */
  orientation: PropTypes.oneOf(["horizontal", "vertical"]),

  /** Definition of separator's thickness. */
  weight: PropTypes.oneOf(["large", "medium", "small"]),
};

Separator.propTypes = separatorPropTypes;
